import React, { useCallback, useEffect, useState, useRef } from "react";
import { UserPlus, UserCheck, Shield, CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import { Spinner } from "../../components/ui/spinner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { allCountries } from "country-telephone-data";
import { approveUser, getUsers, inviteUser, updateUserRole } from "../../lib/api";
import { useToast } from "../../contexts/ToastContext";
import { useAuth } from "../../contexts/AuthContext";
import { formatDate, sameEmail } from "../../lib/utils";

const ROLE_VARIANT = {
  FleetAdmin: "info",
  ReleaseManager: "success",
  SupportEngineer: "warning",
  // Read-only roles (003 Role Gate): view access only, never issued download URLs.
  Nurse: "secondary",
  Patient: "muted",
};
const ROLES = ["FleetAdmin", "ReleaseManager", "SupportEngineer", "Nurse", "Patient"];
const PAGE_SIZE = 10;

// Account lifecycle (TP-SAF-WEB-TS-08-TC-0003): an account has no login until a
// Fleet Admin approves it after the invitee acknowledges. Rows with no status
// predate the field and are existing, active accounts.
const STATUS_DISPLAY = {
  pending:      { label: "Invited",           variant: "warning" },
  acknowledged: { label: "Awaiting approval", variant: "info" },
};
const ACTIVE_STATUS = { label: "Active", variant: "success" };
const isActive = (status) => !STATUS_DISPLAY[status];

// Derived from country-telephone-data — covers all ~250 countries/territories.
// Filters out entries without a dialCode and deduplicates by dial code + iso2.
const COUNTRY_CODES = allCountries
  .filter((c) => c.dialCode && c.iso2)
  .map((c) => ({
    code: `+${c.dialCode}`,
    iso:  c.iso2.toLowerCase(),
    name: c.name,
  }));

const inviteSchema = z.object({
  first_name: z.string().min(1, "First name required"),
  middle_name: z.string().optional(),
  last_name: z.string().min(1, "Last name required"),
  email: z.string().email("Valid email required"),
  country_code: z.string().min(1, "Select a country code"),
  local_number: z.string().regex(/^\d{6,14}$/, "Enter digits only, no spaces or dashes"),
  role: z.enum(["FleetAdmin", "ReleaseManager", "SupportEngineer", "Nurse", "Patient"]),
});

export default function UserManagementPage() {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, totalRecords: 0, totalPages: 1 });
  const [updatingRoleEmail, setUpdatingRoleEmail] = useState(null);
  const [roleUpdateSuccess, setRoleUpdateSuccess] = useState(null);
  const [pendingOnly, setPendingOnly] = useState(false);
  const [approvingId, setApprovingId] = useState(null);
  const [codeSearch, setCodeSearch] = useState("");
  const [codeDropOpen, setCodeDropOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const codeDropRef = useRef(null);

  useEffect(() => {
    function handleOutside(e) {
      if (codeDropRef.current && !codeDropRef.current.contains(e.target)) {
        setCodeDropOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  // Normalise a raw user object from the API into the shape the table expects.
  function normaliseUser(u) {
    // Handle Cognito ListUsers format where attributes are in an array.
    const attrs = {};
    if (Array.isArray(u.Attributes)) {
      u.Attributes.forEach(({ Name, Value }) => { attrs[Name] = Value; });
    }
    // Split fullname into first/last for display.
    const fullnameParts = (u.fullname || u.name || "").trim().split(/\s+/);
    return {
      sub:         u.sub        ?? u.Username   ?? u.username   ?? u.id     ?? attrs.sub ?? u.email,
      email:       u.email      ?? attrs.email  ?? u.Email      ?? "",
      first_name:  u.first_name ?? attrs.given_name  ?? u.given_name  ?? u.firstName ?? fullnameParts[0] ?? "",
      middle_name: u.middle_name ?? attrs.middle_name ?? "",
      last_name:   u.last_name  ?? attrs.family_name ?? u.family_name ?? u.lastName  ?? (fullnameParts.length > 1 ? fullnameParts.slice(1).join(" ") : "") ?? "",
      name:        u.fullname   ?? u.name       ?? attrs.name   ?? "",
      role:        u.current_role ?? u.role     ?? attrs["custom:role"] ?? u.Role ?? "",
      createdAt:   u.joined     ?? u.createdAt  ?? u.UserCreateDate ?? u.created_at ?? u.CreatedAt ?? null,
      status:      u.status     ?? u.UserStatus ?? "",
      id:          u.id         ?? null,
    };
  }

  const fetchUsersPage = useCallback((page = 1) => {
    setLoading(true);
    getUsers({ page, limit: PAGE_SIZE, status: pendingOnly ? "pending" : undefined })
      .then((raw) => {
        const rawUsers = Array.isArray(raw) ? raw : (raw?.users ?? []);
        const pageInfo = raw?.pagination ?? {};
        setUsers(rawUsers.map(normaliseUser));
        setPagination({
          page: pageInfo.page ?? page,
          limit: pageInfo.limit ?? PAGE_SIZE,
          totalRecords: pageInfo.total_records ?? pageInfo.totalRecords ?? rawUsers.length,
          totalPages: pageInfo.total_pages ?? pageInfo.totalPages ?? 1,
        });
      })
      .catch(() => {
        setUsers([]);
        setPagination({ page: 1, limit: PAGE_SIZE, totalRecords: 0, totalPages: 1 });
      })
      .finally(() => setLoading(false));
  }, [pendingOnly]);

  useEffect(() => {
    fetchUsersPage(1);
  }, [fetchUsersPage]);

  const { register, handleSubmit, setValue, reset, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(inviteSchema),
  });

  const onInvite = async (data) => {
    try {
      const newUser = await inviteUser({ ...data, phone: data.country_code + data.local_number });
      setUsers((prev) => [...prev, { ...newUser, createdAt: new Date().toISOString(), lastLogin: null }]);
      setInviteOpen(false);
      reset();
      setSelectedCode(null);
      setCodeSearch("");
      toast({ title: "User invited", description: `Invitation sent to ${data.email}`, variant: "success" });
    } catch {
      toast({ title: "Invite failed", variant: "destructive" });
    }
  };

  const handleApprove = async (u) => {
    setApprovingId(u.id);
    try {
      const result = await approveUser(u.id);
      toast({
        title: result?.role_applied === false ? "Approved, role not applied" : "User approved",
        description: result?.message || `Login details sent to ${u.email}`,
        variant: result?.role_applied === false ? "destructive" : "success",
      });
      fetchUsersPage(pagination.page);
    } catch (error) {
      toast({
        title: "Approval failed",
        description: error?.message || "Unable to approve user.",
        variant: "destructive",
      });
    } finally {
      setApprovingId(null);
    }
  };

  const handleRoleChange = async (email, role) => {
    // Belt and braces: the row's control is disabled for your own account, so
    // this only fires if that ever regresses. The endpoint refuses it either way.
    if (sameEmail(email, currentUser?.email)) {
      toast({
        title: "You cannot change your own role",
        description: "Ask another Fleet Admin to change it for you.",
        variant: "destructive",
      });
      return;
    }
    setUpdatingRoleEmail(email);
    try {
      const updated = await updateUserRole(email, role);
      setUsers((prev) => prev.map((u) => (
        u.email === email ? { ...u, role: updated?.current_role || role } : u
      )));
      setRoleUpdateSuccess({
        email: updated?.email || email,
        role: updated?.current_role || role,
        message: updated?.message || "Role updated successfully",
      });
      // The permission change landed in Cognito but the copy this table reads
      // back did not. Saying nothing would show a success dialog and then revert
      // the badge on the next reload — the exact mismatch this endpoint fixes.
      if (updated?.display_sync === "failed") {
        toast({
          title: "Role changed, but the list may look stale",
          description:
            "Permissions are updated. This table can still show the old role until the change is re-applied.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Role update failed",
        description: error?.message || "Unable to update role.",
        variant: "destructive",
      });
    } finally {
      setUpdatingRoleEmail(null);
    }
  };

  return (
    <div className="space-y-5">
      {updatingRoleEmail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/70 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3 rounded-2xl bg-white px-8 py-6 shadow-xl border border-slate-200">
            <Spinner size="lg" />
            <p className="text-sm font-medium text-slate-600">Updating role...</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">User Management</h2>
          <p className="text-sm text-slate-500">
            {pagination.totalRecords} {pendingOnly ? "accounts not yet activated" : "users"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant={pendingOnly ? "default" : "outline"}
            onClick={() => setPendingOnly((v) => !v)}
            title="Accounts invited or awaiting approval, which cannot sign in yet"
          >
            {pendingOnly ? "Show all users" : "Pending accounts"}
          </Button>
          <Button onClick={() => setInviteOpen(true)}>
            <UserPlus className="mr-2 h-4 w-4" /> Invite User
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Change Role</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-20">
                    <div className="flex items-center justify-center">
                      <Spinner size="lg" />
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!loading && users.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-sm text-slate-500">{pendingOnly ? "No pending accounts." : "No users found."}</TableCell></TableRow>
              )}
              {!loading && users.map((u, index) => {
                const isSelf = sameEmail(u.email, currentUser?.email);
                const active = isActive(u.status);
                const statusDisplay = STATUS_DISPLAY[u.status] || ACTIVE_STATUS;
                return (
                <TableRow key={`${u.sub || u.email}-${u.createdAt || index}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium text-sm">
                        {[u.first_name, u.middle_name, u.last_name].filter(Boolean).join(" ") || u.name}
                      </p>
                      <p className="text-xs text-slate-400">{u.email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Shield className="h-3.5 w-3.5 text-slate-400" />
                      <Badge variant={ROLE_VARIANT[u.role] || "muted"}>{u.role}</Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Badge variant={statusDisplay.variant}>{statusDisplay.label}</Badge>
                      {u.status === "acknowledged" && u.id && !isSelf && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          disabled={Boolean(approvingId)}
                          onClick={() => handleApprove(u)}
                        >
                          {approvingId === u.id
                            ? <Spinner size="sm" />
                            : <><UserCheck className="mr-1 h-3.5 w-3.5" /> Approve</>}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{formatDate(u.createdAt)}</TableCell>
                  <TableCell>
                    {/* The title sits on the wrapper, not the trigger: browsers
                        suppress hover events on a disabled button, so a tooltip
                        on the trigger itself would never appear. */}
                    <span
                      title={isSelf
                        ? "You cannot change your own role"
                        : (!active ? "Available once the account is approved" : undefined)}
                      className="inline-block"
                    >
                      <Select
                        value={u.role}
                        onValueChange={(r) => handleRoleChange(u.email, r)}
                        disabled={Boolean(updatingRoleEmail) || isSelf || !active}
                      >
                        <SelectTrigger className="h-8 w-40 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map((r) => <SelectItem key={r} value={r} className="text-xs">{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </span>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {pagination.totalPages > 1 && (
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={loading || pagination.page <= 1}
              onClick={() => fetchUsersPage(pagination.page - 1)}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Prev
            </Button>
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((p) => {
              const current = pagination.page;
              const total = pagination.totalPages;
              const show = p === 1 || p === total || Math.abs(p - current) <= 1;
              const ellipsisBefore = p === current - 2 && current - 2 > 1;
              const ellipsisAfter = p === current + 2 && current + 2 < total;
              if (ellipsisBefore || ellipsisAfter) {
                return <span key={p} className="px-1 text-sm text-slate-400">...</span>;
              }
              if (!show) return null;
              return (
                <Button
                  key={p}
                  type="button"
                  variant={p === current ? "default" : "outline"}
                  size="sm"
                  disabled={loading}
                  onClick={() => fetchUsersPage(p)}
                  className="min-w-9"
                >
                  {p}
                </Button>
              );
            })}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={loading || pagination.page >= pagination.totalPages}
              onClick={() => fetchUsersPage(pagination.page + 1)}
            >
              Next
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
          {/* Page count removed per design — pagination controls remain above */}
        </div>
      )}

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Invite User</DialogTitle>
            <DialogDescription>
              Fill in the details below. Cognito will handle the user's initial login flow.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(onInvite)} className="space-y-3 py-2 overflow-y-auto flex-1 pr-1">
            <div className="space-y-1.5">
              <Label htmlFor="first_name">First Name</Label>
              <Input id="first_name" placeholder="Jane" {...register("first_name")} />
              {errors.first_name && <p className="text-xs text-red-600">{errors.first_name.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="middle_name">
                Middle Name <span className="text-slate-400 text-xs">(optional)</span>
              </Label>
              <Input id="middle_name" placeholder="Marie" {...register("middle_name")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="last_name">Last Name</Label>
              <Input id="last_name" placeholder="Smith" {...register("last_name")} />
              {errors.last_name && <p className="text-xs text-red-600">{errors.last_name.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inv-email">Email</Label>
              <Input id="inv-email" type="email" placeholder="jane@tetra.med" {...register("email")} />
              {errors.email && <p className="text-xs text-red-600">{errors.email.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Mobile Number</Label>
              <div className="flex gap-2">
                {/* Searchable country code picker */}
                <div className="relative w-36 shrink-0" ref={codeDropRef}>
                  <button
                    type="button"
                    onClick={() => { setCodeDropOpen((o) => !o); setCodeSearch(""); }}
                    className="flex items-center gap-1.5 w-full h-9 px-3 rounded-md border border-input bg-white text-sm shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#08148C]/40"
                  >
                    {selectedCode ? (
                      <>
                        <img src={`https://flagcdn.com/${selectedCode.iso}.svg`} alt={selectedCode.name} width={18} height={13} className="rounded-sm object-cover shrink-0" />
                        <span className="font-medium">{selectedCode.code}</span>
                      </>
                    ) : (
                      <span className="text-slate-400">+Code</span>
                    )}
                    <svg className="ml-auto h-3.5 w-3.5 text-slate-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" /></svg>
                  </button>
                  {codeDropOpen && (
                    <div className="absolute z-50 mt-1 w-64 rounded-md border border-slate-200 bg-white shadow-lg">
                      <div className="p-2 border-b border-slate-100">
                        <Input
                          autoFocus
                          placeholder="Search country or code…"
                          value={codeSearch}
                          onChange={(e) => setCodeSearch(e.target.value)}
                          className="h-8 text-xs"
                        />
                      </div>
                      <ul className="max-h-48 overflow-y-auto py-1">
                        {COUNTRY_CODES.filter(({ code, name }) =>
                          name.toLowerCase().includes(codeSearch.toLowerCase()) ||
                          code.includes(codeSearch)
                        ).map(({ code, iso, name }) => (
                          <li key={`${code}-${iso}`}>
                            {/* The handler lives on a real <button>, not on the <li>:
                                putting it on the list item trips S6847, and adding
                                role="button" instead would trip S6819. onMouseDown
                                (not onClick) is deliberate — it fires before the
                                input's blur closes the dropdown. */}
                            <button
                              type="button"
                              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left cursor-pointer hover:bg-slate-50"
                              onMouseDown={() => {
                                setSelectedCode({ code, iso, name });
                                setValue("country_code", code, { shouldValidate: true });
                                setCodeDropOpen(false);
                                setCodeSearch("");
                              }}
                            >
                              <img src={`https://flagcdn.com/${iso}.svg`} alt={name} width={18} height={13} className="rounded-sm object-cover shrink-0" />
                              <span className="font-medium">{code}</span>
                              <span className="text-slate-400 text-xs truncate">{name}</span>
                            </button>
                          </li>
                        ))}
                        {COUNTRY_CODES.filter(({ code, name }) =>
                          name.toLowerCase().includes(codeSearch.toLowerCase()) ||
                          code.includes(codeSearch)
                        ).length === 0 && (
                          <li className="px-3 py-2 text-xs text-slate-400">No results</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
                <Input id="inv-phone" type="tel" placeholder="91234567" className="flex-1" {...register("local_number")} />
              </div>
              {errors.country_code && <p className="text-xs text-red-600">{errors.country_code.message}</p>}
              {errors.local_number && <p className="text-xs text-red-600">{errors.local_number.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select onValueChange={(v) => setValue("role", v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select role…" />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              {errors.role && <p className="text-xs text-red-600">Role is required</p>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setInviteOpen(false); reset(); setSelectedCode(null); setCodeSearch(""); }}>Cancel</Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? <Spinner size="lg" className="mr-2" /> : <UserPlus className="mr-2 h-4 w-4" />}
                Send Invite
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(roleUpdateSuccess)} onOpenChange={(open) => { if (!open) setRoleUpdateSuccess(null); }}>
        <DialogContent className="sm:max-w-sm">
          <div className="flex flex-col items-center gap-3 py-2 text-center">
            <CheckCircle2 className="h-14 w-14 text-emerald-500" />
            <div className="space-y-1">
              <h3 className="text-base font-semibold text-slate-900">Role Updated</h3>
              <p className="text-sm text-slate-500">
                {roleUpdateSuccess
                  ? <><span className="font-medium text-slate-700">{roleUpdateSuccess.email}</span> is now <span className="font-medium text-slate-700">{roleUpdateSuccess.role}</span>.</>
                  : ""}
              </p>
            </div>
          </div>
          <DialogFooter className="sm:justify-center">
            <Button type="button" className="w-full" onClick={() => setRoleUpdateSuccess(null)}>OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
